/* Purkal — homepage behaviours: hero carousel, story stitch animation,
   and the Patchwork Game modal. Each block is independent and no-ops when
   its section is absent from the page. */
(function () {
  'use strict';

  /* Motion policy: `reduced` gates AUTONOMOUS motion only (hero autoplay).
     Scroll-linked effects — the story thread, smooth anchor scrolling — keep
     running, since they only move as a direct response to the user's own
     scrolling. Android Chrome reports reduced motion whenever Battery Saver is
     on, so gating everything would flatten the page for many real visitors.

     Tracked live rather than read once, so toggling Battery Saver mid-session
     takes effect without a reload. */
  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduced = motionQuery.matches;
  var motionListeners = [];

  function onMotionChange(e) {
    reduced = e.matches;
    for (var i = 0; i < motionListeners.length; i++) motionListeners[i](reduced);
  }

  if (motionQuery.addEventListener) {
    motionQuery.addEventListener('change', onMotionChange);
  } else if (motionQuery.addListener) {
    motionQuery.addListener(onMotionChange); /* older Android Chrome */
  }

  /* ---------------- ticker ----------------
     Normally a pure-CSS marquee. Under reduced motion the slide is switched
     off (see purkal.css), which would otherwise leave only the first message
     visible — so cycle through them with a cross-fade instead. */
  function initTicker(root) {
    if (root.dataset.pkInit) return;
    root.dataset.pkInit = '1';

    var track = root.querySelector('.pk-ticker__track');
    if (!track) return;

    /* Only the first set; the duplicate is aria-hidden and display:none here. */
    var msgs = track.querySelectorAll('span:not([aria-hidden])');
    if (msgs.length === 0) return;

    var idx = 0;
    var timer = null;
    var interval = parseInt(root.dataset.fadeSpeed, 10) || 4000;

    function show(n) {
      for (var i = 0; i < msgs.length; i++) {
        if (i === n) msgs[i].classList.add('pk-ticker__msg--on');
        else msgs[i].classList.remove('pk-ticker__msg--on');
      }
    }

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function apply(isReduced) {
      stop();
      if (!isReduced) {
        /* Hand back to the CSS marquee. */
        for (var i = 0; i < msgs.length; i++) msgs[i].classList.remove('pk-ticker__msg--on');
        return;
      }
      idx = 0;
      show(idx);
      if (msgs.length < 2) return;
      timer = setInterval(function () {
        idx = (idx + 1) % msgs.length;
        show(idx);
      }, interval);
    }

    apply(reduced);
    motionListeners.push(apply);
  }

  /* ---------------- hero carousel ---------------- */
  function initHero(root) {
    if (root.dataset.pkInit) return;
    root.dataset.pkInit = '1';

    var track = root.querySelector('[data-pk-hero-track]');
    var dotsWrap = root.querySelector('[data-pk-hero-dots]');
    if (!track || !dotsWrap) return;

    dotsWrap.innerHTML = '';
    var slides = track.querySelectorAll('.pk-hero__slide');
    if (slides.length < 2) {
      dotsWrap.remove();
      return;
    }

    var autoplay = root.dataset.autoplay === 'true';
    var interval = parseInt(root.dataset.speed, 10) || 6500;
    var dots = [];
    var cur = 0;
    var timer = null;

    for (var i = 0; i < slides.length; i++) {
      var d = document.createElement('button');
      d.type = 'button';
      d.dataset.i = i;
      d.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      dotsWrap.appendChild(d);
      dots.push(d);
    }

    function mark() {
      dots.forEach(function (d, n) {
        d.classList.toggle('is-on', n === cur);
        d.setAttribute('aria-current', n === cur ? 'true' : 'false');
      });
    }

    function goTo(i, smooth) {
      cur = (i + slides.length) % slides.length;
      /* Dot clicks are user-initiated, so they stay smooth; only the
         self-advancing timer below is gated by the motion preference. */
      track.scrollTo({
        left: cur * track.clientWidth,
        behavior: smooth === false ? 'auto' : 'smooth'
      });
      mark();
    }

    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        goTo(+d.dataset.i);
        rest();
      });
    });

    track.addEventListener(
      'scroll',
      function () {
        var n = Math.round(track.scrollLeft / track.clientWidth);
        if (n !== cur) {
          cur = n;
          mark();
        }
      },
      { passive: true }
    );

    function play() {
      if (reduced || !autoplay) return;
      stop();
      timer = setInterval(function () {
        goTo(cur + 1);
      }, interval);
    }
    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }
    function rest() {
      stop();
      play();
    }

    track.addEventListener('pointerdown', stop);
    track.addEventListener('pointerup', rest);
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', rest);
    window.addEventListener('resize', function () {
      goTo(cur, false);
    });

    mark();
    play();

    /* Start/stop the timer if Battery Saver is toggled mid-session. */
    motionListeners.push(function (isReduced) {
      if (isReduced) stop();
      else play();
    });
  }

  /* ---------------- story: reveal + stitching thread ---------------- */
  function initStory(root) {
    if (root.dataset.pkInit) return;
    root.dataset.pkInit = '1';

    var wrap = root.querySelector('[data-pk-stitch]');
    if (!wrap) return;

    var beats = root.querySelectorAll('.pk-beat');
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              e.target.classList.add('is-in');
              io.unobserve(e.target);
            }
          });
        },
        { threshold: 0.2 }
      );
      beats.forEach(function (el) {
        io.observe(el);
      });
    } else {
      beats.forEach(function (el) {
        el.classList.add('is-in');
      });
    }

    var svg = wrap.querySelector('[data-pk-stitch-svg]');
    var guide = wrap.querySelector('[data-pk-guide]');
    var thread = wrap.querySelector('[data-pk-thread]');
    var needle = wrap.querySelector('[data-pk-needle]');
    if (!svg || !guide || !thread) return;

    var len = 0;

    function build() {
      var H = wrap.offsetHeight;
      var N = Math.max(beats.length, 2);
      if (!H) return;
      svg.setAttribute('viewBox', '0 0 60 ' + H);
      svg.style.height = H + 'px';
      var seg = H / N;
      var d = 'M30 6';
      for (var i = 0; i < N; i++) {
        var x1 = i % 2 === 0 ? 52 : 8;
        d +=
          ' C ' + x1 + ' ' + (seg * i + seg * 0.35) +
          ', ' + (60 - x1) + ' ' + (seg * i + seg * 0.65) +
          ', 30 ' + Math.min(seg * (i + 1), H - 6);
      }
      guide.setAttribute('d', d);
      thread.setAttribute('d', d);
      len = thread.getTotalLength();
      thread.style.strokeDasharray = len;
      sew();
    }

    /* Scroll-linked: the thread only advances as the user scrolls, so it runs
       regardless of the reduced-motion preference. */
    function sew() {
      if (!len) return;
      var r = wrap.getBoundingClientRect();
      var p = Math.min(Math.max((window.innerHeight * 0.8 - r.top) / r.height, 0), 1);
      thread.style.strokeDashoffset = len * (1 - p);
      if (needle) {
        if (p > 0 && p < 1) {
          var pt = thread.getPointAtLength(len * p);
          needle.setAttribute('transform', 'translate(' + pt.x + ',' + pt.y + ')');
          needle.style.opacity = 1;
        } else {
          needle.style.opacity = 0;
        }
      }
    }

    build();
    window.addEventListener('scroll', sew, { passive: true });
    window.addEventListener('resize', build);
    window.addEventListener('load', build);
    if (window.ResizeObserver) new ResizeObserver(build).observe(wrap);
  }

  /* ---------------- patchwork game modal ---------------- */
  function initGame() {
    var modal = document.getElementById('pk-game-modal');

    /* The modal ships with the game section, which lives on the homepage.
       Elsewhere (e.g. the header button) send people there instead. */
    if (!modal) {
      document.querySelectorAll('[data-pk-play]').forEach(function (el) {
        if (el.dataset.pkPlayBound) return;
        el.dataset.pkPlayBound = '1';
        el.addEventListener('click', function (e) {
          e.preventDefault();
          window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + '#play';
        });
      });
      return;
    }

    var mount = modal.querySelector('[data-pk-game-mount]');
    var closeBtn = modal.querySelector('[data-pk-game-close]');
    var src = modal.dataset.gameSrc;
    var lastFocus = null;
    var loading = false;

    function mountGame() {
      if (mount.firstChild || loading) return;
      loading = true;
      mount.innerHTML = '<div class="pk-game-mount__loading">Threading the needle…</div>';

      function render() {
        var frame = document.createElement('iframe');
        frame.title = 'The Patchwork Game';
        frame.setAttribute('sandbox', 'allow-scripts');
        frame.srcdoc = window.PurkalGameHTML;
        mount.innerHTML = '';
        mount.appendChild(frame);
      }

      if (window.PurkalGameHTML) {
        render();
        return;
      }
      var s = document.createElement('script');
      s.src = src;
      s.onload = render;
      s.onerror = function () {
        mount.innerHTML =
          '<div class="pk-game-mount__loading">Sorry — the game could not be loaded.</div>';
      };
      document.head.appendChild(s);
    }

    function open() {
      lastFocus = document.activeElement;
      mountGame();
      modal.classList.add('is-open');
      modal.removeAttribute('aria-hidden');
      document.body.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    document.querySelectorAll('[data-pk-play]').forEach(function (el) {
      if (el.dataset.pkPlayBound) return;
      el.dataset.pkPlayBound = '1';
      el.addEventListener('click', function (e) {
        e.preventDefault();
        open();
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);
    modal.addEventListener('click', function (e) {
      if (e.target === modal) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
  }

  function boot() {
    document.querySelectorAll('.pk-ticker').forEach(initTicker);
    document.querySelectorAll('[data-pk-hero]').forEach(initHero);
    document.querySelectorAll('[data-pk-story]').forEach(initStory);
    initGame();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-init when a section is re-rendered in the theme editor. */
  document.addEventListener('shopify:section:load', boot);
})();
